import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MaintenanceCostsService } from '../services/costs.service';
import type { 
  MaintenanceCost, 
  CostFilters 
} from '../types/maintenance-costs.types';
import { useState } from 'react';

export function useCosts(companyId: string, initialFilters: CostFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<CostFilters>({
    type: 'all',
    status: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['maintenance-costs', companyId, filters],
    queryFn: () => MaintenanceCostsService.getCosts(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['maintenance-costs-stats', companyId],
    queryFn: () => MaintenanceCostsService.getCostStats(companyId),
    enabled: !!companyId,
  });

  const createCostMutation = useMutation({
    mutationFn: (data: Partial<MaintenanceCost>) => MaintenanceCostsService.createCost(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-costs'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-costs-stats'] });
    },
  });

  const approveCostMutation = useMutation({
    mutationFn: ({ id, approverId }: { id: string, approverId: string }) => 
      MaintenanceCostsService.approveCost(id, approverId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-costs'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-costs', 'detail', variables.id] });
    },
  });

  return {
    ...query,
    costs: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoadingStats: statsQuery.isLoading,
    filters,
    setFilters,
    createCost: createCostMutation.mutateAsync,
    approveCost: approveCostMutation.mutateAsync,
    isCreating: createCostMutation.isPending,
    isApproving: approveCostMutation.isPending,
  };
}

export function useCostDetail(id?: string) {
  return useQuery({
    queryKey: ['maintenance-costs', 'detail', id],
    queryFn: () => MaintenanceCostsService.getCostDetail(id!),
    enabled: !!id,
  });
}
